import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import i18next, {
    TRANSLATION_LANGUAGES,
    TRANSLATION_LANGUAGES_HEAD
} from '../../base/i18n/i18next';
import { isLocalParticipantModerator } from '../../base/participants';
import { showErrorNotification } from '../../notifications';
import { selectTranslationLanguage } from '../actions';


/**
 * Higher Order Component taking in a concrete LanguageSelector component and
 * augmenting it with state/behavior common to both web and native implementations.
 *
 * @param {React.Component} Component - The concrete component.
 * @returns {React.Component}
 */
const AbstractLanguageSelectorDialog = Component => () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const noLanguageLabel = 'transcribing.subtitlesOff';

    const language = useSelector(state => state['features/subtitles']._language);
    const subtitlesData = useSelector(state => state['features/subtitles']);
    const showDefaultErrMsg = useSelector(state => state['features/subtitles']._showDefaultTranscriptionLang);

    const OngoingTranscriptionLanguage = useSelector(state => state['features/base/conference']?.conference?.options?.config?.transcriptionLanguage);


    const subtitles = language ?? noLanguageLabel;

    const transcription = useSelector(state => state['features/base/config'].transcription);
    const translationEnabled = transcription?.translationEnabled;
    const { confSettings } = useSelector(state => state['features/base/jwt']);
    const isTranscribing = useSelector(state => state['features/transcribing'].isTranscribing);
    const translationLanguagesHead = transcription?.translationLanguagesHead ?? TRANSLATION_LANGUAGES_HEAD;
    const languagesHead = translationLanguagesHead?.map((lang: string) => `${lang}`);
    let isLocalModerator;

    useSelector(state => {
        isLocalModerator = isLocalParticipantModerator(state);
    });

    // The off and the head languages are always on the top of the list. But once you are selecting
    // a language from the translationLanguages, that language is moved under the fixedItems list,
    // until a new languages is selected. FixedItems keep their positions.
    const fixedItems = [ noLanguageLabel, ...languagesHead ];
    const translationLanguages = transcription?.translationLanguages ?? TRANSLATION_LANGUAGES;
    const languages = translationLanguages
        .map((lang: string) => `${lang}`)
        .filter((lang: string) => !(lang === subtitles || languagesHead?.includes(lang)));
    const listItems = (fixedItems?.includes(subtitles)
        ? [ ...fixedItems, ...languages ]
        : [ ...fixedItems, subtitles, ...languages ])
        .map((lang, index) => {
            return {
                id: lang + index,
                lang,
                selected: lang === subtitles
            };
        });


    const onLanguageSelected = useCallback((value: string) => {
        const selectedLanguage = value === noLanguageLabel ? null : value;
        const enabled = Boolean(selectedLanguage);
        const displaySubtitles = enabled;

        if (enabled === true) {
            if (transcription?.enabled && Boolean(selectedLanguage) === false) {

                if (confSettings?.TRANSCRIPTION_QUOTA_EXCEEDED === 1) {
                    if (isLocalModerator) {
                        dispatch(showErrorNotification(
                {
                    descriptionKey: i18next.t('transcribing.transcriptionQuotaExceeded'),
                    titleKey: i18next.t('transcribing.transcriptionQuotaExceededTitle')
                }));
                    }
                } else {
                    dispatch(selectTranslationLanguage(enabled, displaySubtitles, selectedLanguage));
                }
            } else {
                dispatch(selectTranslationLanguage(enabled, displaySubtitles, selectedLanguage));
            }
        } else {
            dispatch(selectTranslationLanguage(enabled, displaySubtitles, selectedLanguage));
        }
    }, [ language ]);


    return (
        <Component
            OngoingTranscriptionLanguage = { OngoingTranscriptionLanguage }
            confSettings = { confSettings }
            dispatch = { dispatch }
            isLocalModerator = { isLocalModerator }
            isTranscribing = { isTranscribing }
            language = { language }
            listItems = { listItems }
            onLanguageSelected = { onLanguageSelected }
            showDefaultErrMsg = { showDefaultErrMsg }
            subtitles = { subtitles }
            subtitlesData = { subtitlesData }
            t = { t }
            transcription = { transcription }
            translationEnabled = { translationEnabled } />
    );
};

export default AbstractLanguageSelectorDialog;
